export type ChatbotAiKnowledgeSource = 'file' | 'text';
export type ChatbotAiKnowledgeModalMode = 'create' | 'file' | 'text';

export const CHATBOT_AI_KNOWLEDGE_CONFIG = {
  pageSize: 50,
  minChunkSize: 200,
  maxChunkSize: 2000,
  maxChunkOverlap: 500,
  defaultMaxChunkSize: 500,
  defaultChunkOverlap: 50,
} as const;

export const CHATBOT_AI_KNOWLEDGE_FILE_STATUS = {
  processed: 2,
  error: 3,
  processing: 4,
} as const;

export interface IChatbotAiKnowledge {
  id: string;
  name: string;
  businessId: number | string;
  type: number;
  platformId?: string | null;
  description: string;
  createdById: string;
  createdByName?: string;
  createdAt: number;
  fileCount?: number;
  textCount?: number;
  assistantCount?: number;
  /** Tiến trình nạp gần nhất bên AI Core (model mới) */
  aiCore?: IChatbotAiKnowledgeAiCore | null;
}

export type ChatbotAiCoreRunStatus = 'queued' | 'running' | 'done' | 'stopped' | 'failed';

export interface IChatbotAiKnowledgeAiCore {
  runId: string;
  status: ChatbotAiCoreRunStatus;
  percent: number;
  updatedAt?: number;
  /** Đã từng nạp lại toàn bộ kho (Nạp lại); không có thì kho chỉ được đồng bộ từng mục, mục cũ có thể thiếu */
  full?: boolean;
}

/** Đang nạp (chờ hoặc đang chạy) */
export function isAiCoreRunning(aiCore: IChatbotAiKnowledgeAiCore | null | undefined): boolean {
  return aiCore?.status === 'queued' || aiCore?.status === 'running';
}

/** Kho đã nạp đủ cho AI Core: đã nạp lại toàn bộ (full) và lần nạp gần nhất không lỗi, không dừng */
export function isAiCoreLoaded(aiCore: IChatbotAiKnowledgeAiCore | null | undefined): boolean {
  return !!aiCore?.full && aiCore.status !== 'failed' && aiCore.status !== 'stopped';
}

export interface IChatbotAiCoreProgress {
  runId: string;
  status: ChatbotAiCoreRunStatus;
  percent: number;
  busy?: boolean;
  sources?: { total: number; done: number; failed: number; skipped: number };
  chunks?: { total: number; done: number };
}

export interface IChatbotAiKnowledgeFile {
  id: string;
  name: string;
  businessId: number | string;
  type: number;
  status: number;
  fileName: string;
  size: number | string;
  url: string;
  knowledgeId: string;
  createdById: string;
  createdAt: number;
}

export interface IChatbotAiKnowledgeText {
  id: string;
  knowledgeId: string;
  title: string;
  text: string;
  kind?: 'text' | 'faq';
  embeddingIds: string[];
  createdById: string;
  createdAt: number;
}

export interface IChatbotAiKnowledgeCursorData<T> {
  result: T[];
  next: unknown[] | false;
  aiCoreEnabled?: boolean;
}

export interface IChatbotAiKnowledgeListParams {
  size?: number;
  next?: string;
  ids?: string[];
}

export interface IChatbotAiKnowledgeContentListParams {
  knowledgeId?: string;
  size?: number;
  next?: string;
  title?: string;
}

export interface IChatbotAiKnowledgeTextParams {
  id?: string;
  title: string;
  text: string;
  /** faq: title là câu hỏi, text là câu trả lời */
  kind?: 'text' | 'faq';
}

/** Tệp đã được tải lên cloud, sẵn sàng để server tạo knowledge và embedding. */
export interface IChatbotAiKnowledgeUploadFile {
  id?: string;
  name: string;
  businessId: number | string;
  fileName: string;
  size: number;
  url: string;
}

export interface IChatbotAiKnowledgeUpsertParams {
  knowledgeId?: string;
  knowledgeName?: string;
  knowledgeDescription?: string;
  maxChunkSize?: number;
  chunkOverlap?: number;
  files?: IChatbotAiKnowledgeUploadFile[];
  knowledgeText?: IChatbotAiKnowledgeTextParams;
}

export interface IChatbotAiKnowledgeUpsertData {
  message: string;
  knowledge: IChatbotAiKnowledge;
  files: IChatbotAiKnowledgeFile[] | null;
  text: IChatbotAiKnowledgeText | null;
}

export interface IChatbotAiKnowledgeSaveFileEmbeddingParams {
  fileId: string;
  knowledgeId: string;
  url: string;
  chunk_size: number;
  overlap: number;
}

export interface IChatbotAiKnowledgeSaveFileEmbeddingData {
  message: string;
  fileId: string;
  errors: string[];
}

export interface IChatbotAiKnowledgeIdentityParams {
  id: string;
}

export interface IChatbotAiKnowledgeMessageData {
  message: string;
}
